import type { Chip } from "../core/chip";

type Props = {
  chip: Chip;
  x: number;
  y: number;
  width: number;
  height: number;
  active?: boolean;
  color: string;
  onSelect: (chipId: string) => void;
};

/** Clipped-octagon path; book destinations use a wider, shallower chamfer (book frame). */
function framePath(
  x: number,
  y: number,
  width: number,
  height: number,
  book: boolean,
): string {
  const chamfer = book
    ? Math.max(10, Math.round(Math.min(width, height) * 0.06))
    : Math.max(14, Math.round(Math.min(width, height) * 0.1));
  return `M ${x + chamfer} ${y} L ${x + width - chamfer} ${y} L ${x + width} ${y + chamfer} L ${x + width} ${y + height - chamfer} L ${x + width - chamfer} ${y + height} L ${x + chamfer} ${y + height} L ${x} ${y + height - chamfer} L ${x} ${y + chamfer} Z`;
}

export function ChipNode({
  chip,
  x,
  y,
  width,
  height,
  active,
  color,
  onSelect,
}: Props) {
  const book = chip.id === "arcopedia";
  const d = framePath(x, y, width, height, book);
  const labelY = y + height * (book ? 0.68 : 0.72);
  const stateY = y + height * (book ? 0.84 : 0.86);
  const iconScale = Math.min(width, height) / (book ? 92 : 108);
  const iconSize = 36 * iconScale;
  const stateLabel = book ? "RECOMMENDED" : chip.state;
  const stateFill =
    chip.id === "verify" ? "var(--cyan)" : book ? color : "white";

  return (
    <g
      class="node-card"
      onClick={() => onSelect(chip.id)}
      role="button"
      aria-label={chip.title}
    >
      <path
        d={d}
        fill="rgba(9,16,29,0.9)"
        stroke={color}
        stroke-width={book ? 2.4 : active ? 3.5 : 1.8}
      />
      <g
        class="node-icon"
        transform={`translate(${x + width / 2 - iconSize / 2} ${y + height * (book ? 0.16 : 0.12)}) scale(${iconScale})`}
      >
        {chip.id === "arcopedia" && (
          <path
            d="M4 8c6-4 12-2 16 2v18c-4-4-10-6-16-2zM32 8c-6-4-12-2-16 2v18c4-4 10-6 16-2zM20 10v18"
            stroke={color}
            fill="none"
          />
        )}
        {chip.id === "loremine" && (
          <path d="m3 8 15-6 15 6-15 6zM3 15l15 6 15-6M3 22l15 6 15-6" />
        )}
        {chip.id === "artemis" && (
          <path d="M18 5a10 10 0 1 0 0 20 10 10 0 1 0 0-20Zm0 6a4 4 0 1 0 0 8 4 4 0 1 0 0-8Zm10-3 4 1-3 3" />
        )}
        {chip.id === "build" && (
          <path d="m18 2 13 8v16l-13 8-13-8V10zM18 17l13-7M18 17 5 10M18 17v17" />
        )}
        {chip.id === "verify" && (
          <>
            <path
              d="M18 2 31 7v10c0 8-5 12-13 15C10 29 5 25 5 17V7z"
              stroke={color}
              fill="none"
            />
            <path
              d="M11 17l5 5 9-10"
              stroke="var(--green)"
              fill="none"
              stroke-width="2.2"
            />
          </>
        )}
      </g>
      <path
        d={d}
        fill="none"
        stroke={active ? "#dff9ff" : color}
        stroke-width={book ? 1.2 : 1}
        opacity="0.55"
      />
      <text
        x={x + width / 2}
        y={labelY}
        text-anchor="middle"
        class="node-label"
        fill={color}
      >
        {chip.title}
      </text>
      <text
        x={x + width / 2}
        y={stateY}
        text-anchor="middle"
        class="meta"
        fill={stateFill}
      >
        {stateLabel}
      </text>
    </g>
  );
}
